import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import styles from './CustomerSearchInput.module.scss';

interface BaseCustomer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

interface CustomerSearchInputProps {
  customers: BaseCustomer[];
  value: string;
  onChange: (customerId: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

function CustomerSearchInput({
  customers,
  value,
  onChange,
  placeholder = 'Search for a customer...',
  required = false,
  disabled = false,
}: CustomerSearchInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find selected customer
  const selectedCustomer = customers.find((c) => c.id === value);

  // Filter customers
  const filteredCustomers = customers.filter((customer) => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        customer.name.toLowerCase().includes(query) ||
        (customer.email && customer.email.toLowerCase().includes(query)) ||
        (customer.phone && customer.phone.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!value) {
      setSearchQuery('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing
    if (value && query !== selectedCustomer?.name) {
      onChange('');
    }
  };

  // Handle customer selection
  const handleSelect = (customerId: string) => {
    onChange(customerId);
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear button
  const handleClear = () => {
    onChange('');
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredCustomers.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCustomers.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredCustomers.length
        ) {
          handleSelect(filteredCustomers[highlightedIndex].id);
        } else if (filteredCustomers.length === 1) {
          handleSelect(filteredCustomers[0].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Display value: show customer name if selected, otherwise show search query
  const displayValue = selectedCustomer ? selectedCustomer.name : searchQuery;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.inputWrapper}>
        <Search size={16} className={styles.searchIcon} />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={styles.input}
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear selection"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && filteredCustomers.length > 0 && (
        <div className={styles.suggestions}>
          {filteredCustomers.map((customer, index) => (
            <button
              key={customer.id}
              type="button"
              onClick={() => handleSelect(customer.id)}
              className={`${styles.suggestionItem} ${
                index === highlightedIndex ? styles.highlighted : ''
              }`}
            >
              <div className={styles.customerInfo}>
                <span className={styles.customerName}>{customer.name}</span>
                {(customer.email || customer.phone) && (
                  <span className={styles.customerContact}>
                    {customer.email || customer.phone}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomerSearchInput;
